/**
 * CreateIndividualOrgModal Component
 *
 * Модальное окно для создания организации с индивидуальными занятиями.
 * Обязательные поля: Название и Адрес.
 */

import React, { useState } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  LoadingOverlay
} from '@mantine/core';
import { IconHome, IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../../theme/designTokens';


export function CreateIndividualOrgModal({ opened, onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [addressError, setAddressError] = useState('');

  const handleSubmit = async () => {
    let hasError = false;

    if (!name.trim()) {
      setNameError('Введите название');
      hasError = true;
    }
    if (!address.trim()) {
      setAddressError('Введите адрес');
      hasError = true;
    }
    if (hasError) return;

    setLoading(true);
    setNameError('');
    setAddressError('');

    try {
      const response = await fetch('/api/organizations/individual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          address: address.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Не удалось создать организацию');
      }

      const created = await response.json();

      notifications.show({
        title: 'Организация создана',
        message: `Индивидуальные занятия "${name}" успешно созданы`,
        color: 'green',
      });

      setName('');
      setAddress('');
      onClose();

      if (onSuccess) {
        onSuccess(created);
      }
    } catch (err) {
      console.error('Error creating individual org:', err);
      setNameError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setAddress('');
    setNameError('');
    setAddressError('');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconHome size={20} color={tokens.colors.brand.primary} />
          <Text fw={600}>Создать индивидуальные занятия</Text>
        </Group>
      }
      size="md"
      centered
    >
      <LoadingOverlay visible={loading} />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Индивидуальные занятия — на дому у клиента. Оплата, группы и
          расписание будут привязаны к этой организации.
        </Text>

        <TextInput
          label="Название"
          placeholder="Например: Индивидуальные — Иванов И.И."
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError('');
          }}
          error={nameError}
          required
          data-autofocus
        />

        <TextInput
          label="Адрес"
          placeholder="г. Жуковский, ул. Ломоносова д. 25, кв. 12"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            setAddressError('');
          }}
          error={addressError}
          required
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            Отмена
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={loading}
          >
            Создать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default CreateIndividualOrgModal;
